import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { CalendarDays, Download, Upload, Loader2, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function PrimaveraPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [history, setHistory] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgR, projR, histR] = await Promise.all([
        axios.get(`${API}/admin/primavera-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/primavera/projects`, { headers: getAuthHeaders() }),
        axios.get(`${API}/primavera/sync-history`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgR.data);
      setProjects(projR.data || []);
      setHistory(histR.data || []);
    } catch { toast.error("Failed to load P6 config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/primavera-config`, config, { headers: getAuthHeaders() });
      toast.success("Primavera P6 configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleSync = async (direction) => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/primavera/sync?direction=${direction}`, {}, { headers: getAuthHeaders() });
      toast.success(`Sync complete — Projects: ${r.data.projects}, Activities: ${r.data.activities}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading Primavera P6 configuration...</div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="primavera-page">
        <PageHeader title="Oracle Primavera P6" subtitle="Sync project schedules and activities with Primavera EPPM" testId="p6-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="p6-save-btn">{saving ? "Saving..." : "Save Configuration"}</Button>
        </PageHeader>

        <IntegrationHealthBadge integration="primavera" onTestConnection={async () => { const r = await axios.post(`${API}/admin/primavera-test`, {}, { headers: getAuthHeaders() }); return r.data; }} />

        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2"><CalendarDays size={16} /> Connection</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="w-24">Enabled</Label>
              <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="p6-enabled-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div>
              <Label>P6 EPPM Server URL *</Label>
              <Input value={config.server_url || ""} onChange={e => update("server_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://p6-server.company.com" data-testid="p6-server-url" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Username</Label><Input value={config.username || ""} onChange={e => update("username", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="p6-username" /></div>
              <div><Label>Password</Label><Input type="password" value={config.password || ""} onChange={e => update("password", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="p6-password" /></div>
              <div><Label>Database Name</Label><Input value={config.database_name || ""} onChange={e => update("database_name", e.target.value)} className="mt-1 rounded-none text-sm" placeholder="Optional" data-testid="p6-database" /></div>
            </div>
            <div className="flex gap-4">
              {[{ key: "sync_projects", label: "Sync Projects" }, { key: "sync_activities", label: "Sync Activities" }].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={config[opt.key] || false} onChange={() => update(opt.key, !config[opt.key])} className="rounded border-[#CBD5E1]" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {config.enabled && (
          <div className="bg-[#0F172A] p-6 text-white">
            <div className="flex items-center justify-between">
              <div><h2 className="font-heading font-bold text-xl">Manual Sync</h2><p className="text-[#94A3B8] text-sm mt-1">Pull projects/activities from P6 or push work orders.</p></div>
              <div className="flex gap-2">
                <Button onClick={() => handleSync("pull")} disabled={syncing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid="p6-pull-btn">{syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}Pull</Button>
                <Button onClick={() => handleSync("push")} disabled={syncing} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="p6-push-btn">{syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push WOs</Button>
                <Button onClick={() => handleSync("both")} disabled={syncing} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none" data-testid="p6-sync-both-btn">{syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}Full Sync</Button>
              </div>
            </div>
          </div>
        )}

        {projects.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3">Projects ({projects.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>{["ID", "Name", "Status", "Start", "Finish"].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-bold uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {projects.slice(0, 20).map(p => (
                    <tr key={p.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2 font-mono text-xs">{p.project_id}</td>
                      <td className="px-3 py-2 font-medium">{p.name}</td>
                      <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs font-bold bg-[#EFF6FF] text-[#1E40AF] uppercase">{p.status}</span></td>
                      <td className="px-3 py-2 text-xs text-[#64748B]">{p.start_date ? new Date(p.start_date).toLocaleDateString() : "-"}</td>
                      <td className="px-3 py-2 text-xs text-[#64748B]">{p.finish_date ? new Date(p.finish_date).toLocaleDateString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><Clock size={16} /> Sync History</h3>
            <div className="space-y-1">
              {history.slice(0, 15).map(h => (
                <div key={h.id} className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E2E8F0] text-sm">
                  <span className="font-mono text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString()}</span>
                  <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF6FF] text-[#1E40AF] uppercase">{h.direction}</span>
                  <span className="text-xs">Projects: {h.projects} | Activities: {h.activities}</span>
                  {h.errors > 0 && <span className="text-xs text-[#DC2626]">{h.errors} errors</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
